import React, { FC, memo, useEffect, useState, ReactNode } from "react";
import { SvgIcon } from "../svg-icon";
import FlickityComponent, { FlickityOptions } from "react-flickity-component";
import { TabletAndLarger, MobileAndTabletPortrait } from "../responsive";

interface ICarouselProps {
    header?: ReactNode;
    lowHeaderSpacing?: boolean;
    insideBrokenContainer?: boolean;
    children: ReactNode | ReactNode[];
    wrapAround?: boolean;
}

export const Carousel: FC<ICarouselProps> = memo((props: ICarouselProps) => {
    const { children, header, insideBrokenContainer, lowHeaderSpacing, wrapAround = false } = props;

    const childrenCount = React.Children.count(children);
    const [flickity, setFlickity] = useState<FlickityComponent | null>(null);
    const [leftArrowActive, setLeftArrowActive] = useState(false);
    const [rightArrowActive, setRightArrowActive] = useState(childrenCount > 1);


    // #region FLICKITY FUNCTIONS


    const onStaticClick = (_event?: Event, _pointer?: Element | Touch, _cellElement?: Element, cellIndex?: number) => {
        if (typeof cellIndex !== 'undefined') {
            flickity?.select(cellIndex);
        }
    };

    const onChange = (index?: number) => {
        if (typeof index !== "undefined") {
            setLeftArrowActive(index > 0);
            setRightArrowActive(childrenCount > 0 && index !== childrenCount - 1);
        }
    };

    function onArrowLeftClick() {
        flickity?.previous();
    }

    function onArrowRightClick() {
        flickity?.next();
    }

    // #endregion Flickity functions

    useEffect(() => {
        flickity?.on("staticClick", onStaticClick);
        flickity?.on("change", onChange);


        return () => {
            if (flickity) {
                if (onStaticClick) {
                    flickity.off("staticClick", onStaticClick);
                }

                if (onChange) {
                    flickity.off("change", onChange);
                }

                setFlickity(null);
            }
        };
    }, [flickity]);

    return (
        <section className={`carousel${insideBrokenContainer ? ' carousel--inside-break-container' : ''}`}>
            <div className={`carousel-header${lowHeaderSpacing ? ' carousel-header--low-spacing' : ''}`}>
                {header && (
                    <div className="carousel-header__content">
                        {header}
                    </div>
                )}
                <TabletAndLarger>
                    <div className="carousel-controls">
                        <button
                            onClick={onArrowLeftClick}
                            type="button"
                            aria-label="Previous"
                        >
                            <SvgIcon
                                iconName="arrow_left"
                                className={`carousel-controls__arrow ${leftArrowActive ? "carousel-controls__arrow--active" : ""}`} />
                        </button>
                        <button
                            onClick={onArrowRightClick}
                            type="button"
                            aria-label="Next"
                        >
                            <SvgIcon
                                iconName="arrow_right"
                                className={`carousel-controls__arrow ${rightArrowActive ? "carousel-controls__arrow--active" : ""}`} />
                        </button>
                    </div>
                </TabletAndLarger>
            </div>
            <MobileAndTabletPortrait>
                <section className="carousel-slider">
                    {children}
                    {insideBrokenContainer && (<div className="carousel-slider__spacer"></div>)}
                </section>
            </MobileAndTabletPortrait>
            <TabletAndLarger>
                <FlickityComponent
                    flickityRef={(ref: FlickityComponent) => setFlickity(ref)}
                    className="carousel__flickity"
                    options={{
                        freeScroll: true,
                        contain: true,
                        pageDots: false,
                        cellAlign: "left",
                        wrapAround: wrapAround,
                    } as FlickityOptions}
                >
                    {children}
                </FlickityComponent>
            </TabletAndLarger>
        </section>
    );
});
